import { useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import { updateUserEmail } from "../../store/reducers/auth-reducer"; // Ensure the correct path
import MenuItemHeader from "../../components/MenuItemHeader/MenuItemHeader";
import styles from "./Account.module.scss";
import AccountInfoCard from "../../components/AccountInfoCard/AccountInfoCard";
import AccountSubscriptionCard from "../../components/AccountSubscriptionCard/AccountSubscriptionCard";
import { accountInfoData } from "./constants";
import Modal from "../../components/modal/modal";
import SendCodeModal from "../../components/modals/SendCodeModal/SendCodeModal";
import ChangePasswordModal from "../../components/modals/ChangePasswordModal/ChangePasswordModal";
import ChangeEmailModal from "../../components/modals/ChangeEmailModal/ChangeEmailModal";
import EmailConfirmModal from "../../components/modals/EmailConfirmModal/EmailConfirmModal";
import EmailCodeConfirmModal from "../../components/modals/EmailCodeConfirmModal/EmailCodeConfirmModal";
import ManageSubscriptionModal from "../../components/modals/ManageSubscriptionModal/ManageSubscription";
import PauseSubscriptionModal from "../../components/modals/PauseSubscriptionModal/PauseSubscriptionModal";
import SuccessfullyModal from "../../components/modals/SuccessfullyModal/SuccessfullyModal";
import UpdatePaymentModal from "../../components/modals/UpdatePaymentModal/UpdatePaymentModal";
import CreditCardInfoModal from "../../components/modals/CreditCardInfoModal/CreditCardInfoModal";
import ProcessingModal from "../../components/modals/ProcessingModal/ProcessingModal";
import PayPalModal from "../../components/modals/PayPalModal/PayPalModal";
import CancelSubscriptionModal from "../../components/modals/CancelSubscriptionModal/CancelSubscriptionModal";
import UpdatePasswordModal from "../../components/modals/UpdatePasswordModal/UpdatePasswordModal";
import { useTranslation } from "react-i18next";
import { useAppSelector } from "../../store/hooks";
import { useGetLicensesQuery, useGetSubscriptionsQuery } from "../../store/services/details-api";
import { subscriptionData } from "../../types/details-api-types"; // Ensure you have the correct path and type
import LoaderCard from "../../components/Loader/LoaderCard";
import RenewalModal from "../../components/modals/RenewalModal/RenewalModal";
import SuccessfullyRenewalModal from "../../components/modals/SuccessfullyRenewalModal/SuccessfullyRenewalModal";
import CancelSubscriptionOfferModal from "../../components/modals/CancelSubscriptionOfferModal/CancelSubscriptionOfferModal";
import { trackEvent } from "../../utils/analytics";
import { EVENT_NAMES } from "../../types/event-names";
import CodeEmailChangeModal from "../../components/modals/CodeEmailChangeModal/CodeEmailChangeModal";
import { isCodeLogin } from "../../utils/codeLogin";

const AccountPage = () => {
  const [isModalEmailVisible, setIsModalEmailVisible] = useState(false);
  const [isModalPasswordVisible, setIsModalPasswordVisible] = useState(false);
  const [isModalSendLinkVisible, setIsModalSendLinkVisible] = useState(false);
  const [isModalNewPasswordVisible, setIsModalNewPasswordVisible] = useState(false);
  const [isModalConfirmVisible, setIsModalConfirmVisible] = useState(false);
  const [isModalCodeConfirmVisible, setIsModalCodeConfirmVisible] = useState(false);
  const [isModalManageSubVisible, setIsModalManageSubVisible] = useState(false);
  const [isModalPauseSubVisible, setIsModalPauseSubVisible] = useState(false);
  const [isModalCardInfoVisible, setIsModalCardInfoVisible] = useState(false);
  const [isModalUpdatePayVisible, setIsModalUpdatePayVisible] = useState(false);
  const [isModalPayPalVisible, setIsUpdatePayPalVisible] = useState(false);
  const [isModalCancelSubVisible, setIsCancelSubVisible] = useState(false);
  const [isModalCancelSubProcesVisible, setIsCancelSubProcesVisible] = useState(false);
  const [isModalCancelSubSuccessfulyVisible, setIsCancelSubSuccessfulyVisible] = useState(false);
  const [isModalPayPalSuccessfulyVisible, setIsUpdatePayPalSuccessfulyVisible] = useState(false);
  const [isModalUpdatePaySuccessfullyVisible, setIsModalUpdatePaySuccessfullyVisible] = useState(false);
  const [isModalProcessingVisible, setIsModalProcessingVisible] = useState(false);
  const [isModalPauseConfirmVisible, setIsModalPauseConfirmVisible] = useState(false);
  const [isModalRenewalVisible, setIsModalRenewalVisible] = useState(false);
  const [isModalSuccessfullyVisible, setIsModalSuccessfullyVisible] = useState(false);
  const [isCancellationConfirmation, setIsCancellationConfirmation] = useState(false);
  const [isModalCancelSubOfferVisible, setIsModalCancelSubOfferVisible] = useState(false);
  const [isModalCodeEmailVisible, setIsModalCodeEmailVisible] = useState(false);

  // Signed in with an email code: no password, and the email change uses the code-based flow
  const codeLogin = isCodeLogin();

  const [newEmailAddress, setNewEmailAddress] = useState("");
  const [selectedSubscription, setSelectedSubscription] = useState<subscriptionData | null>(null);
  const [loading, setLoading] = useState(false);

  const dispatch = useDispatch();
  const authState = useAppSelector((state) => state.auth);
  const { t } = useTranslation();

  const { data: subscriptions, refetch, isFetching: subsIsFetching } = useGetSubscriptionsQuery(null);
  const { data: licenses, refetch: licenseRefetch, isFetching: licenseIsFetching } = useGetLicensesQuery(null);
  const isFetching = subsIsFetching || licenseIsFetching;

  const data = useMemo(() => {
    if (!subscriptions || !licenses) return [];

    return subscriptions
      .map((d) => ({
        ...d,
        licenses: licenses.filter((item) => item.subscription_id === d.subscription_id),
        isExpired: licenses
          .filter((license) => license.subscription_id === d.subscription_id)
          .every((license) => new Date(license.expiration_date).getTime() < new Date().getTime()),
      }))
      .sort((a, b) => Number(a.isExpired) - Number(b.isExpired));
  }, [licenses, subscriptions]);

  // Data in backend need time to refresh
  const RefreshData = async () => {
    setLoading(true);
    setTimeout(async () => {
      await Promise.all([refetch(), licenseRefetch()]);
      setLoading(false);
    }, 4500);
  };

  const handleEmailUpdate = (newEmail: string) => {
    dispatch(updateUserEmail(newEmail));
  };

  const confirmationModalData = useMemo(() => {
    const data = {
      subTitle: t("Please check your inbox for an email containing a magic link to reset your password."),
    };

    if (isCancellationConfirmation) {
      data.subTitle = t("For safety measures, we sent a 6-digit code to your email.");
    }

    return data;
  }, [isCancellationConfirmation, t]);

  useEffect(() => {
    trackEvent(EVENT_NAMES.SCREEN_ACCOUNT_VIEWED, { screen_name: "Account" });
  }, []);

  return (
    <div className={styles.root}>
      {/* Change Password Modal */}
      <Modal isOpen={isModalPasswordVisible} setIsVisible={() => setIsModalPasswordVisible(false)}>
        <ChangePasswordModal
          setIsModalPasswordVisible={setIsModalPasswordVisible}
          setIsModalSendLinkVisible={(a) => {
            setIsModalSendLinkVisible(a);
            setIsCancellationConfirmation(false);
          }}
        />
      </Modal>

      {/* Send Code Modal */}
      <Modal isOpen={isModalSendLinkVisible} setIsVisible={() => setIsModalSendLinkVisible(false)}>
        <SendCodeModal
          title={t("Check Your Email for the 6-digit code")}
          subtitle={confirmationModalData.subTitle}
          message={t("If you don't see the email, make sure to look in your spam/junk folder.")}
          setIsModalSendLinkVisible={setIsModalSendLinkVisible}
          setIsModalNewPasswordVisible={setIsModalNewPasswordVisible}
          isForgotPasswordPage={false}
          isCancellationConfirmation={isCancellationConfirmation}
          setNextFlowModal={setIsCancelSubProcesVisible}
          subscriptionItem={selectedSubscription}
          callback={(action) => {
            RefreshData();

            if (action === "success") {
              setIsCancelSubProcesVisible(false);
              setIsCancelSubSuccessfulyVisible(true);
            } else {
              setIsCancelSubVisible(true);
              setIsCancelSubProcesVisible(false);
            }
          }}
        />
      </Modal>

      {/* Update Password Modal */}
      <Modal isOpen={isModalNewPasswordVisible} setIsVisible={() => setIsModalNewPasswordVisible(false)}>
        <UpdatePasswordModal isForgotPasswordPage={false} setIsVisible={setIsModalNewPasswordVisible} />
      </Modal>

      {/* Change Email Modal */}
      <Modal isOpen={isModalEmailVisible} setIsVisible={() => setIsModalEmailVisible(false)}>
        <ChangeEmailModal
          setIsModalEmailVisible={setIsModalEmailVisible}
          setIsModalConfirmVisible={setIsModalCodeConfirmVisible}
          setNewEmailAddress={setNewEmailAddress}
        />
      </Modal>

      {/* Email Confirm Modal */}
      <Modal isOpen={isModalConfirmVisible} setIsVisible={() => setIsModalConfirmVisible(false)}>
        <EmailConfirmModal setIsModalConfirmVisible={setIsModalConfirmVisible} />
      </Modal>

      {/* Email Code Confirm Modal */}
      <Modal isOpen={isModalCodeConfirmVisible} setIsVisible={() => setIsModalConfirmVisible(false)}>
        <EmailCodeConfirmModal
          setIsModalCodeConfirmVisible={setIsModalCodeConfirmVisible}
          length={6}
          onComplete={(value) => console.info(value)}
          setNewEmailAddress={setNewEmailAddress}
          newEmailAddress={newEmailAddress}
          onEmailUpdate={handleEmailUpdate}
        />
      </Modal>

      {/* Change Email Modal for email-code sign-in */}
      <Modal isOpen={isModalCodeEmailVisible} setIsVisible={() => setIsModalCodeEmailVisible(false)}>
        <CodeEmailChangeModal setIsVisible={setIsModalCodeEmailVisible} onEmailUpdate={handleEmailUpdate} />
      </Modal>

      {/* Renewal Modal */}
      <Modal isOpen={isModalRenewalVisible} setIsVisible={() => setIsModalRenewalVisible(false)}>
        <RenewalModal
          setIsModalPauseConfirmVisible={setIsModalRenewalVisible}
          setIsModalProcessingVisible={setIsModalProcessingVisible}
          setIsModalSuccessfullyVisible={setIsModalSuccessfullyVisible}
          data={selectedSubscription as subscriptionData}
        />
      </Modal>

      {/* Successfully Renewal Modal */}
      <Modal isOpen={isModalSuccessfullyVisible} setIsVisible={() => setIsModalSuccessfullyVisible(false)}>
        <SuccessfullyRenewalModal
          setIsModalPauseConfirmVisible={() => setIsModalSuccessfullyVisible(false)}
          refreshData={() => RefreshData()}
          data={selectedSubscription as subscriptionData}
        />
      </Modal>

      {/* Manage Subscription Modal */}
      <Modal isOpen={isModalManageSubVisible} setIsVisible={() => setIsModalManageSubVisible(false)}>
        <ManageSubscriptionModal
          setIsModalManageSubVisible={setIsModalManageSubVisible}
          setIsModalPauseSubVisible={setIsModalPauseSubVisible}
          setIsModalUpdatePayVisible={setIsModalUpdatePayVisible}
          setIsCancelSubVisible={setIsCancelSubVisible}
          setIsModalRenewalVisible={setIsModalRenewalVisible}
          subscriptionItem={selectedSubscription}
        />
      </Modal>

      {/* Pause Subscription Modal */}
      <Modal isOpen={isModalPauseSubVisible} setIsVisible={() => setIsModalPauseSubVisible(false)}>
        <PauseSubscriptionModal
          setIsModalPauseSubVisible={setIsModalPauseSubVisible}
          setIsModalPauseConfirmVisible={setIsModalPauseConfirmVisible}
          subscriptionItem={selectedSubscription}
          refetch={RefreshData}
        />
      </Modal>

      {/* Pause Subscription Confirmation Modal */}
      <Modal isOpen={isModalPauseConfirmVisible} setIsVisible={() => setIsModalPauseConfirmVisible(false)}>
        <SuccessfullyModal
          setIsModalPauseConfirmVisible={setIsModalPauseConfirmVisible}
          tittle={t("Subscription Paused")}
          subtitle={t(
            "Your Fortect subscription has been paused. You will still have access to all features until the end of your current billing period. We've sent a confirmation email to your registered email address."
          )}
        />
      </Modal>

      {/* Update Payment Modal */}
      <Modal isOpen={isModalUpdatePayVisible} setIsVisible={() => setIsModalUpdatePayVisible(false)}>
        <UpdatePaymentModal
          setIsModalUpdatePayVisible={setIsModalUpdatePayVisible}
          setIsModalCardInfoVisible={setIsModalCardInfoVisible}
          setIsUpdatePayPalVisible={setIsUpdatePayPalVisible}
          setIsModalUpdatePaySuccessfullyVisible={setIsModalUpdatePaySuccessfullyVisible}
          subscriptionItem={selectedSubscription}
        />
      </Modal>

      {/* Credit Card Info Modal */}
      <Modal
        isOpen={isModalCardInfoVisible}
        setIsVisible={() => {
          setIsModalCardInfoVisible(false);
        }}
      >
        <CreditCardInfoModal
          setIsModalCardInfoVisible={setIsModalCardInfoVisible}
          setIsModalUpdatePayVisible={setIsModalUpdatePayVisible}
          setIsModalProcessingVisible={setIsModalProcessingVisible}
          setIsModalUpdatePaySuccessfullyVisible={setIsModalUpdatePaySuccessfullyVisible}
        />
      </Modal>

      {/* Processing Modal */}
      <Modal
        isOpen={isModalProcessingVisible}
        setIsVisible={() => {
          setIsModalProcessingVisible(false);
        }}
      >
        <ProcessingModal setIsModalProcessingVisible={setIsModalProcessingVisible} />
      </Modal>

      {/* Update Payment Successful Modal */}
      <Modal
        isOpen={isModalUpdatePaySuccessfullyVisible}
        setIsVisible={() => setIsModalUpdatePaySuccessfullyVisible(false)}
      >
        <SuccessfullyModal
          setIsModalPauseConfirmVisible={setIsModalUpdatePaySuccessfullyVisible}
          tittle={t("Payment Method Updated Successfully")}
          subtitle={t(
            "Your payment method has been successfully updated. Your Fortect subscription will now be charged using the new payment"
          )}
        />
      </Modal>

      {/* PayPal Modal */}
      <Modal
        isOpen={isModalPayPalVisible}
        setIsVisible={() => {
          setIsUpdatePayPalVisible(false);
        }}
      >
        <PayPalModal setIsUpdatePayPalVisible={setIsUpdatePayPalVisible} />
      </Modal>

      {/* PayPal Successful Modal */}
      <Modal
        isOpen={isModalPayPalSuccessfulyVisible}
        setIsVisible={() => {
          setIsUpdatePayPalSuccessfulyVisible(false);
        }}
      >
        <SuccessfullyModal
          setIsModalPauseConfirmVisible={setIsModalUpdatePaySuccessfullyVisible}
          tittle={t("Payment Method Updated Successfully")}
          subtitle={t(
            "Your payment method has been successfully updated. Your Fortect subscription will now be charged using the new payment"
          )}
        />
      </Modal>

      {/* Cancel Subscription Modal */}
      <Modal isOpen={isModalCancelSubVisible} setIsVisible={() => setIsCancelSubVisible(false)}>
        <CancelSubscriptionModal
          setIsModalPauseSubVisible={setIsModalPauseSubVisible}
          setIsCancelSubVisible={setIsCancelSubVisible}
          subscriptionItem={selectedSubscription as subscriptionData}
          callbackAction={(action, data) => {
            if (action === "deactivate") {
              setSelectedSubscription(data as subscriptionData);
              setIsCancellationConfirmation(true);
              setIsModalSendLinkVisible(true);
            } else if (action === "offer") {
              setIsModalCancelSubOfferVisible(true);
              setSelectedSubscription(data as subscriptionData);
              setIsCancelSubVisible(false);
            }
          }}
        />
      </Modal>

      {/* Cancel Subscription Offer Modal */}
      <Modal
        overflow
        overflowDisableClose
        isOpen={isModalCancelSubOfferVisible}
        setIsVisible={() => setIsModalCancelSubOfferVisible(false)}
      >
        <CancelSubscriptionOfferModal
          subscriptionItem={selectedSubscription as subscriptionData}
          callbackAction={(action, data) => {
            if (action === "deactivate") {
              setSelectedSubscription(data as subscriptionData);
              setIsCancellationConfirmation(true);
              setIsModalSendLinkVisible(true);
              setIsModalCancelSubOfferVisible(false);
            } else if (action === "close") {
              setIsModalCancelSubOfferVisible(false);
            }
          }}
        />
      </Modal>

      {/* Cancel Subscription Processing Modal */}
      <Modal isOpen={isModalCancelSubProcesVisible} setIsVisible={() => setIsCancelSubProcesVisible(false)}>
        <ProcessingModal setIsModalProcessingVisible={setIsCancelSubProcesVisible} />
      </Modal>

      {/* Cancel Subscription Successful Modal */}
      <Modal isOpen={isModalCancelSubSuccessfulyVisible} setIsVisible={() => setIsCancelSubSuccessfulyVisible(false)}>
        <SuccessfullyModal
          setIsModalPauseConfirmVisible={setIsCancelSubSuccessfulyVisible}
          tittle={t("Subscription Cancelled Successfully")}
          subtitle={t(
            "Your Fortect subscription has been cancelled. You will still have access to all features until the end of your current billing period. We've sent a confirmation email to your registered email address."
          )}
        />
      </Modal>

      <MenuItemHeader
        page={t("My Account")}
        subtitle={t("Manage your personal information, preferences, and subscriptions.")}
      />

      <div className={styles.root_info}>
        <AccountInfoCard
          title={t(accountInfoData[0].title)}
          type={t(accountInfoData[0].type)}
          icon={t(accountInfoData[0].icon)}
          value={t(authState.user?.email ?? "No email")}
          changeType={t(accountInfoData[0].change)}
          onClick={() => (codeLogin ? setIsModalCodeEmailVisible(true) : setIsModalEmailVisible(true))}
        />
        {!codeLogin && (
          <AccountInfoCard
            title={t(accountInfoData[1].title)}
            type={t(accountInfoData[1].type)}
            icon={t(accountInfoData[1].icon)}
            value={t(accountInfoData[1].value ?? "")}
            changeType={t(accountInfoData[1].change)}
            onClick={() => setIsModalPasswordVisible(true)}
          />
        )}
      </div>
      <p
        className={styles.root_title}
        // onClick={() => setIsModalManageSubVisible(true)}
      >
        {t("My subscriptions")}
      </p>

      {!loading && !isFetching && data ? (
        data!.map((card, index) => (
          <div key={index}>
            <AccountSubscriptionCard
              cardData={card}
              subNumber={card.subscription_id}
              renewalDate={new Date(card.next_payment).toLocaleDateString()}
              price={card.renewalPrice}
              devicesNumber={card.license.seats}
              devicesInUse={card.license.seatsUsed}
              priceCurrency={card.renewalPriceCurrency}
              refreshData={RefreshData}
              isExpired={card.isExpired}
            />
          </div>
        ))
      ) : (
        <LoaderCard />
      )}
    </div>
  );
};

export default AccountPage;
